import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import { useAuthStore } from '@/store/authStore'
import { refresh } from '@/api/auth'
import { ToastContainer } from '@/components/Toast'
import { ProtectedRoute } from '@/routes/ProtectedRoute'
import { AppLayout } from '@/routes/AppLayout'

import { Login } from '@/pages/Login'
import { Dashboard } from '@/pages/Dashboard'
import { Activity } from '@/pages/Activity'
import { Organizations } from '@/pages/Organizations'
import { OrganizationDetail } from '@/pages/OrganizationDetail'
import { Users } from '@/pages/Users'
import { UserDetail } from '@/pages/UserDetail'
import { Security } from '@/pages/Security'
import { Analytics } from '@/pages/Analytics'
import { SearchResults } from '@/pages/SearchResults'
import { Documents } from '@/pages/Documents'
import { Signatures } from '@/pages/Signatures'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 10_000,
      refetchOnWindowFocus: false,
    },
  },
})

function SessionBootstrap({ children }: { children: React.ReactNode }) {
  const setAccessToken = useAuthStore((s) => s.setAccessToken)
  const setHydrated = useAuthStore((s) => s.setHydrated)

  // On a hard reload, the in-memory access token is gone but the httpOnly
  // refresh cookie may still be valid — try once to silently re-establish
  // the session before deciding whether to show the login page. (Admin
  // profile info re-populates from the next authenticated API response;
  // a dedicated GET /api/root/auth/me would be a clean follow-up.)
  useEffect(() => {
    let cancelled = false
    refresh()
      .then((res) => {
        if (cancelled) return
        setAccessToken(res.accessToken)
      })
      .catch(() => {
        /* no valid session — user will see the login page */
      })
      .finally(() => {
        if (!cancelled) setHydrated()
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return <>{children}</>
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <SessionBootstrap>
          <Routes>
            <Route path="/login" element={<Login />} />

            <Route element={<ProtectedRoute />}>
              <Route element={<AppLayout />}>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/activity" element={<Activity />} />
                <Route path="/organizations" element={<Organizations />} />
                <Route path="/organizations/:id" element={<OrganizationDetail />} />
                <Route path="/users" element={<Users />} />
                <Route path="/users/:id" element={<UserDetail />} />
                <Route path="/security" element={<Security />} />
                <Route path="/analytics" element={<Analytics />} />
                <Route path="/search" element={<SearchResults />} />
                <Route path="/documents" element={<Documents />} />
                <Route path="/signatures" element={<Signatures />} />
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </SessionBootstrap>
      </BrowserRouter>
      <ToastContainer />
    </QueryClientProvider>
  )
}
